/**
 * Homepage - Ultra Performance Optimized Version
 * - Zero axios (native fetch)
 * - Minimal date-fns (deferred)
 * - No framer-motion
 * - Aggressive lazy loading
 * - Static fallback data
 */
import React, { useState, lazy, Suspense, useEffect, memo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSEO from '../hooks/useSEO';
import { OrganizationSchema, WebsiteSchema } from '../components/StructuredData';
import { supportedLanguageCodes, defaultLanguage } from '../i18n/config';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Optimized WebP images - ultra small file sizes for PageSpeed
const HERO_IMAGE = 'https://images.unsplash.com/photo-1683736071800-ad7ed27dc057?w=1000&q=50&fm=webp&fit=crop&auto=format';
const HERO_IMAGE_MOBILE = 'https://images.unsplash.com/photo-1683736071800-ad7ed27dc057?w=480&q=40&fm=webp&fit=crop&auto=format';
const CARD_IMAGE_1 = 'https://images.unsplash.com/photo-1683736071800-ad7ed27dc057?w=300&q=40&fm=webp&fit=crop&auto=format';
const CARD_IMAGE_2 = 'https://images.unsplash.com/photo-1619528395742-4e41ab87403b?w=300&q=40&fm=webp&fit=crop&auto=format';
const CARD_IMAGE_MINIBUS = 'https://customer-assets.emergentagent.com/job_welcome-guests-6/artifacts/stdg6x1q_Foto%20minibus%20elettrico%2015%20Posti%20Bologna%20Inside%206-%20Piazza%20Santo%20Stefano5.jpg';

// Lazy load ALL below-fold sections
const GoogleReviewsSection = lazy(() => import('../components/GoogleReviews').then(m => ({ default: m.GoogleReviewsSection })));
const TestimonialsSection = lazy(() => import('../components/sections/TestimonialsSection'));
const WhyChooseUsSection = lazy(() => import('../components/sections/WhyChooseUsSection'));
const CTASection = lazy(() => import('../components/sections/CTASection'));

// Inline SVG icons to avoid lucide-react bundle
const ArrowRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
  </svg>
);

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

// Minimal section loader
const SectionLoader = () => <div className="py-16 bg-gray-50 animate-pulse" />;

// Memoized Experience Card for performance
const ExperienceCard = memo(({ to, image, title, tagline, desc, duration, maxPeople, price, nextDate }) => (
  <Link to={to} className="group block bg-white rounded-3xl p-5 shadow-lg border border-gray-100 hover:shadow-2xl transition-shadow">
    <div className="relative overflow-hidden rounded-2xl aspect-[4/3] mb-5 bg-gray-100">
      <img
        src={image}
        alt={title}
        className="w-full h-full object-cover"
        loading="lazy"
        decoding="async"
        width={400}
        height={300}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      <div className="absolute bottom-4 left-4 right-4">
        <span className={`${nextDate ? 'bg-brand-red' : 'bg-gray-500'} text-white px-4 py-2 rounded-full text-sm font-medium`}>
          {nextDate || 'Prossime date in arrivo'}
        </span>
      </div>
    </div>
    <h3 className="text-2xl font-serif font-bold text-gray-900 mb-2 group-hover:text-brand-red transition-colors">
      {title}
    </h3>
    <p className="text-brand-red font-medium mb-3">{tagline}</p>
    <p className="text-gray-600 mb-4 line-clamp-2">{desc}</p>
    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
      <div className="flex items-center gap-4 text-sm text-gray-500">
        <span className="flex items-center gap-1"><ClockIcon />{duration}</span>
        <span className="flex items-center gap-1"><UsersIcon />Max {maxPeople}</span>
      </div>
      <p className="text-xl font-bold text-brand-red">€{price}</p>
    </div>
  </Link>
));

const Homepage = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const [departures, setDepartures] = useState({});
  
  const currentLang = supportedLanguageCodes.includes(lang) ? lang : defaultLanguage;
  const localizedPath = (path) => `/${currentLang}${path}`;
  const isItalian = i18n.language === 'it';

  useSEO();

  // Deferred fetch - doesn't block render, loads after 1s
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetch(`${API_URL}/api/departures/upcoming?limit=10`, { signal: controller.signal })
        .then(r => r.json())
        .then(data => {
          const byProduct = {};
          data.forEach(d => {
            if (!byProduct[d.product_id]) byProduct[d.product_id] = d;
          });
          setDepartures(byProduct);
        })
        .catch(() => {});
    }, 1000);
    return () => { clearTimeout(timer); controller.abort(); };
  }, []);

  // Format date simply without date-fns
  const formatDeparture = (dep) => {
    if (!dep) return null;
    try {
      const date = new Date(dep.date + 'T12:00:00');
      const day = date.toLocaleDateString(isItalian ? 'it-IT' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' });
      return `${day} • ${dep.time}`;
    } catch { return null; }
  };

  return (
    <div className="min-h-screen bg-cream">
      <OrganizationSchema />
      <WebsiteSchema />
      
      {/* HERO - Critical, no lazy */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <picture>
            <source media="(max-width: 768px)" srcSet={HERO_IMAGE_MOBILE} type="image/webp" />
            <img
              src={HERO_IMAGE}
              alt="Panorama Bologna"
              className="w-full h-full object-cover"
              fetchPriority="high"
              decoding="sync"
              loading="eager"
              width={1200}
              height={800}
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="max-w-2xl">
            <span className="inline-block bg-brand-red text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              {t('hero.badge')}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-6 leading-tight">
              {t('hero.title')}
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-xl">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to={localizedPath('/experiences')}
                className="inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-red-dark text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors"
              >
                {t('hero.cta')}
                <ArrowRightIcon />
              </Link>
              <Link
                to={localizedPath('/about')}
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold text-lg border border-white/30 transition-colors"
              >
                {t('hero.secondary_cta')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* EXPERIENCES */}
      <section id="experiences" className="py-16 lg:py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-4">
              {t('experiences.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('experiences.subtitle')}
            </p>
          </div>

          {/* Experience Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            <ExperienceCard
              to={localizedPath('/experience/happy-hour-sunset-tour')}
              image={CARD_IMAGE_1}
              title={t('experiences.sunset.name')}
              tagline={t('experiences.sunset.tagline')}
              desc={t('experiences.sunset.short_desc')}
              duration="1.5h"
              maxPeople={47}
              price={35}
              nextDate={formatDeparture(departures['happy-hour-sunset-tour'])}
            />
            <ExperienceCard
              to={localizedPath('/experience/sfoglia-tortellini-experience')}
              image={CARD_IMAGE_2}
              title={t('experiences.tortellini.name')}
              tagline={t('experiences.tortellini.tagline')}
              desc={t('experiences.tortellini.short_desc')}
              duration="3.5h"
              maxPeople={15}
              price={59}
              nextDate={formatDeparture(departures['sfoglia-tortellini-experience'])}
            />
            
            {/* Private Minibus Card */}
            <Link 
              to={localizedPath(isItalian ? '/noleggio-privato-minibus-bologna' : '/private-tour-bologna-electric-minibus')} 
              className="group block bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-5 shadow-lg border border-gray-700 hover:shadow-2xl transition-shadow"
            >
              <div className="relative overflow-hidden rounded-2xl aspect-[4/3] mb-5 bg-gray-800">
                <img
                  src={CARD_IMAGE_MINIBUS}
                  alt={isItalian ? "Noleggio Privato Minibus Elettrico Bologna" : "Private Electric Minibus Rental Bologna"}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  width={400}
                  height={300}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex gap-2">
                  <span className="bg-green-600 text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/>
                    </svg>
                    Eco
                  </span>
                  <span className="bg-amber-500 text-white px-3 py-1.5 rounded-full text-sm font-medium">
                    Premium
                  </span>
                </div>
              </div>
              <h3 className="text-2xl font-serif font-bold text-white mb-2 group-hover:text-red-400 transition-colors">
                {isItalian ? 'Noleggio Privato Minibus' : 'Private Minibus Rental'}
              </h3>
              <p className="text-red-400 font-medium mb-3">
                {isItalian ? 'Tour esclusivo con pickup in hotel' : 'Exclusive tour with hotel pickup'}
              </p>
              <p className="text-gray-300 mb-4 line-clamp-2">
                {isItalian 
                  ? 'Esperienze personalizzate su minibus elettrico. Ideale per gruppi, famiglie e aziende.'
                  : 'Personalized experiences on electric minibus. Ideal for groups, families and companies.'}
              </p>
              <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                <div className="flex items-center gap-4 text-sm text-gray-400">
                  <span className="flex items-center gap-1"><ClockIcon />{isItalian ? 'Flessibile' : 'Flexible'}</span>
                  <span className="flex items-center gap-1"><UsersIcon />Max 14</span>
                </div>
                <p className="text-lg font-bold text-red-400">{isItalian ? 'Su richiesta' : 'On request'}</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* LAZY SECTIONS */}
      <Suspense fallback={<SectionLoader />}>
        <GoogleReviewsSection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <WhyChooseUsSection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <TestimonialsSection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <CTASection />
      </Suspense>
    </div>
  );
};

export default Homepage;
